// src/components/EmptyState.js
// États vides dans le lore — chaque page a son message démoniaque + CTA

import React from "react";
import { Link } from "react-router-dom";
import "./EmptyState.css";

const STATES = {

  // Journal
  journal_no_demon: {
    icon: "👹", jp: "無記録",
    title: "Ton démon n'a pas encore de chroniques.",
    sub: "Il existe. Mais rien n'a encore été inscrit dans le registre.",
    cta: { to: "/awaken", label: "RÉVEILLER MON DÉMON →" },
    cta2: null,
  },
  journal_empty: {
    icon: "📜", jp: "空の記録",
    title: "Le registre est vide.",
    sub: "Chaque session mérite d'être inscrite. Note ta prochaine. Le Chroniqueur la forge en légende.",
    cta: null,
    note: "Commence par noter quelques mots sur ta dernière session dans le champ ci-dessus.",
  },

  // Duel
  duel_no_demon: {
    icon: "⚔️", jp: "決闘待ち",
    title: "Ton démon n'est pas prêt à combattre.",
    sub: "Crée d'abord ton profil démoniaque. Ensuite tu pourras défier n'importe quel Oni.",
    cta: { to: "/awaken", label: "RÉVEILLER MON DÉMON →" },
  },
  duel_no_opponent: {
    icon: "🌑", jp: "対戦相手なし",
    title: "Personne à combattre.",
    sub: "Cherche un utilisateur par son nom, ou crée un profil custom pour affronter un adversaire imaginaire.",
    cta: { to: "/buddiz", label: "TROUVER DES BUDDIZ →" },
  },

  // Social — Buddiz
  buddiz_empty: {
    icon: "🤝", jp: "仲間なし",
    title: "Aucun Buddy encore.",
    sub: "Les démons solitaires sont les plus dangereux. Mais les démons alliés sont inarrêtables. Trouve tes pairs.",
    cta: null,
    note: "Partage ton lien de profil à tes partenaires d'entraînement.",
  },

  // Social — Sessions
  sessions_empty: {
    icon: "📅", jp: "稽古なし",
    title: "Aucune session planifiée.",
    sub: "Un démon sans entraînement est un démon qui régresse. Planifie ton prochain rite.",
    cta: null,
    note: "Crée une session avec le bouton + PLANIFIER.",
  },

  // Social — Clans
  clans_empty: {
    icon: "一", jp: "一族なし",
    title: "Tu n'appartiens à aucun clan.",
    sub: "Les démons solitaires tombent un par un. Un clan les rend invincibles.",
    cta: null,
    note: "Rejoins un clan existant ou fonde le tien.",
  },

  // OniTalk
  // FFTT — no preys
  fftt_no_preys: {
    icon: "🎯", jp: "獲物なし",
    title: "Aucune proie désignée.",
    sub: "Le registre FFTT contient tes futurs adversaires. Cherche-les. Désigne-les. Prépare-toi.",
    cta: null,
    note: "Lance une recherche par nom pour scanner le registre officiel.",
  },

  // Leaderboard
  leaderboard_empty: {
    icon: "⚡", jp: "番付空",
    title: "Le classement est vide cette semaine.",
    sub: "Aucun démon n'a encore marqué son territoire. Sois le premier.",
    cta: { to: "/journal", label: "💬 ONITALK →" },
    cta2: { to: "/duel", label: "⚔️ DUEL →" },
  },

  // Coach
  coach_no_demon: {
    icon: "🎥", jp: "指導不能",
    title: "Le Coach a besoin de ton démon.",
    sub: "Sans profil, le Coach ne peut pas adapter son analyse à ta nature démoniaque.",
    cta: { to: "/awaken", label: "RÉVEILLER MON DÉMON →" },
  },
  coach_no_video: {
    icon: "🎬", jp: "映像なし",
    title: "Pas encore de vidéo analysée.",
    sub: "Colle un lien YouTube d'un match ou upload une vidéo. Le Coach démoniaque entre en scène.",
    cta: null,
    note: "Commence par une vidéo YouTube — un match que tu veux décortiquer.",
  },

  // Profile — no demon
  profile_no_demon: {
    icon: "🌑", jp: "鬼未覚醒",
    title: "Ton démon dort encore.",
    sub: "Il attend depuis longtemps. 5 questions suffisent à le réveiller.",
    cta: { to: "/awaken", label: "RÉVEILLER MON DÉMON →" },
    cta2: { to: "/lore", label: "DÉCOUVRIR LA LÉGENDE" },
  },

  // Evolve — all perks unlocked
  evolve_all_done: {
    icon: "⬆️", jp: "進化完了",
    title: "Tous les perks sont débloqués.",
    sub: "Ton démon a atteint un palier rare. Continue à jouer pour pousser ses stats via OniTalk.",
    cta: { to: "/journal", label: "💬 ONITALK →" },
  },

  // Team Battle
  team_empty: {
    icon: "🔴", jp: "戦争なし",
    title: "Aucune battle en cours.",
    sub: "Les démons n'attendent pas. Crée la première Team Battle et défi un clan adverse.",
    cta: null,
    note: "Utilise + CRÉER pour lancer une battle 2v2 ou 3v3.",
  },
};

export default function EmptyState({ type, className = "" }) {
  const state = STATES[type];
  if (!state) return null;

  return (
    <div className={`empty-state ${className}`}>
      <div className="empty-state-icon">{state.icon}</div>
      {state.jp && <p className="empty-state-jp">{state.jp}</p>}
      <h3 className="empty-state-title">{state.title}</h3>
      <p className="empty-state-sub">{state.sub}</p>
      {state.note && (
        <p className="empty-state-note">{state.note}</p>
      )}
      {(state.cta || state.cta2) && (
        <div className="empty-state-actions">
          {state.cta && (
            <Link to={state.cta.to} className="btn btn-primary btn-sm">
              {state.cta.label}
            </Link>
          )}
          {state.cta2 && (
            <Link to={state.cta2.to} className="btn btn-ghost btn-sm">
              {state.cta2.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// Export des types pour autocomplete
export const EMPTY_STATE_TYPES = Object.keys(STATES);
